// Shared "record / edit payment" dialog — Iter 306/308: the method list comes
// from the Commissions workflow settings (default Applied to invoice / EFT /
// Cheque); an optional partner picker lets Payment history add entries for any partner.
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DEFAULT_PAYMENT_METHODS = ["Applied to invoice", "EFT", "Cheque"];

export function usePaymentMethods() {
  const [methods, setMethods] = useState(DEFAULT_PAYMENT_METHODS);
  useEffect(() => {
    api.get("/commissions/payment-methods").then((r) => { if (r.data.items?.length) setMethods(r.data.items); }).catch(() => {});
  }, []);
  return methods;
}

export function PaymentDialog({ period, editing, suggestedAmount, saving, onClose, onSubmit, partnerOptions, partnerKey }) {
  const [form, setForm] = useState(() => ({
    payment_id:   editing?.payment_id || "",
    partner_key:  partnerKey || editing?.partner_key || "",
    period_label: editing?.period_label || period || "",
    amount:       editing?.amount != null ? String(editing.amount)
                  : (suggestedAmount != null ? String(suggestedAmount) : ""),
    method:       editing?.method || "",
    reference:    editing?.reference || "",
    paid_at:      (editing?.paid_at || new Date().toISOString()).slice(0, 10),
    notes:        editing?.notes || "",
  }));
  const methods = usePaymentMethods();
  const legacyMethod = form.method && !methods.includes(form.method) ? form.method : "";
  const needsPartner = !!partnerOptions && !partnerKey;
  const ready = form.amount && form.period_label && form.method && (!needsPartner || form.partner_key);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit payment" : period ? `Record payment for ${period}` : "Record payment"}</DialogTitle>
          <DialogDescription>Track a payment ({methods.slice(0, 3).join(", ").toLowerCase()}{methods.length > 3 ? ", …" : ""}) against a partner&rsquo;s commissions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {needsPartner && (
            <div>
              <Label className="text-xs">Partner</Label>
              <Select value={form.partner_key} onValueChange={(v) => setForm({ ...form, partner_key: v })}>
                <SelectTrigger data-testid="payment-partner"><SelectValue placeholder="Pick the partner" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {partnerOptions.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Period</Label>
              <Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })}
                     placeholder="2026-06" data-testid="payment-period" />
            </div>
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     data-testid="payment-amount" />
              {!editing && suggestedAmount != null && Number(suggestedAmount) > 0 && (
                <div className="mt-1 text-[10px] text-slate-400">Pre-filled with net payable for this period — confirm or edit before recording.</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method"><SelectValue placeholder="How was it paid?" /></SelectTrigger>
                <SelectContent>
                  {methods.map((m) => <SelectItem key={m} value={m} data-testid={`payment-method-${m.toLowerCase().replace(/\s+/g, "-")}`}>{m}</SelectItem>)}
                  {legacyMethod && <SelectItem value={legacyMethod}>{legacyMethod} (legacy)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                     placeholder={form.method === "Cheque" ? "Cheque #" : form.method === "EFT" ? "EFT / transaction ID" : "Invoice #"}
                     data-testid="payment-reference" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Paid on</Label>
            <Input type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} data-testid="payment-paid-at" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Anything to remember about this payment" data-testid="payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="payment-cancel">Cancel</Button>
          <Button onClick={() => ready && onSubmit(form)} disabled={saving || !ready}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="payment-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save" : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
